import React, { useState } from "react";
import styles from "./Welcome.module.css";
import { FaArrowRight, FaCheckCircle, FaBullseye, FaLock, FaGlobe, FaTimes, FaEnvelope } from "react-icons/fa";
import { useNavigate } from "react-router-dom";

function Welcome() {
  const navigate = useNavigate();
  const handleNavigation = () => {
    navigate("/FORYOU");
  };
  
  const [showTerms, setShowTerms] = useState(false);
  const [showPrivacy, setShowPrivacy] = useState(false);
  const [email, setEmail] = useState('');
  const [emailSubmitted, setEmailSubmitted] = useState(false);
  const [emailError, setEmailError] = useState('');

  // Email validation function
  const isValidEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // Handle email submission
  const handleEmailSubmit = (e) => {
    e.preventDefault();
    setEmailError('');
    
    if (!email.trim()) {
      setEmailError('Please enter your email address');
      return;
    }
    
    if (!isValidEmail(email)) {
      setEmailError('Please enter a valid email address');
      return;
    }

    // Here you would typically send the email to your backend
    console.log('Email submitted:', email);
    
    // Show success state
    setEmailSubmitted(true);
    
    // Reset after 3 seconds (optional)
    setTimeout(() => {
      setEmailSubmitted(false);
      setEmail('');
    }, 3000);
  };

  // Smooth scroll function
  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });
    }
  };

  // Modal component for legal documents
  const Modal = ({ isOpen, onClose, title, children }) => {
    if (!isOpen) return null;

    return (
      <div className={styles.modalOverlay} onClick={onClose}>
        <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
          <div className={styles.modalHeader}>
            <h2>{title}</h2>
            <button onClick={onClose} className={styles.closeButton}>
              <FaTimes />
            </button>
          </div>
          <div className={styles.modalBody}>
            {children}
          </div>
        </div>
      </div>
    );
  };

  return (
    <main className={styles.welcomeContainer}>
      {/* Hero Section */}
      <section className={styles.heroSection}>
        <div className={styles.heroContent}>
          <div className={styles.heroText}>
            {/* Logo - moved higher */}
            <h1 className={`${styles.logo} ${styles.logoElevated}`}>
              <span className="logo-cure">CURE</span>
              {/* <FaHeart className="logo-heart" /> */}
              <span className="logo-iosity">IOSITY</span>
            </h1>
            {/* Tagline */}
            <p className={styles.tagline}>
              Every Cure Starts With Curiosity
            </p>
            {/* Enhanced Headline */}
            <h2 className={styles.headline}>
              Find Your Perfect <span className={styles.highlight}>Clinical Trial</span> Match
            </h2>
            <p className={styles.subHeadline}>
              Clinical research shouldn't be confusing. Explore innovative health studies that may be right for you.
              We make research simple to understand, so you can decide if participating is a good fit.
            </p>
            {/* Call to Action */}
            {/* <button className={styles.ctaButton} onClick={handleNavigation}>
              //Let's Find Trials
              <FaArrowRight className={styles.ctaIcon} />
            </button> */}
          </div>
          <div className={styles.heroVisual}>
            <div className={styles.trustCard}>
              {/* Divider */}
              <div className={styles.divider} />
              {/* Trust Signals */}
              <div className={styles.benefits}>
                <div className={styles.benefit}>
                  <FaCheckCircle className={styles.benefitIcon} />
                  Ethics Board Approved
                </div>
                <div className={styles.benefit}>
                  <FaCheckCircle className={styles.benefitIcon} />
                  Doctor Reviewed
                </div>
                <div className={styles.benefit}>
                  <FaCheckCircle className={styles.benefitIcon} />
                  Personalized Matches
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className={styles.featuresSection}>
        <div className={styles.sectionHeader}>
          <h2 className={styles.sectionTitle}>Why Choose Cureiosity?</h2>
        </div>
        <div className={styles.featuresGrid}>
          <div className={styles.featureCard}>
            <div className={styles.featureIcon}>
              <FaGlobe />
            </div>
            <h3>Global Network</h3>
            <p>Expand your options with clinical trials from trusted research institutions around the world.</p>
          </div>
          <div className={styles.featureCard}>
            <div className={styles.featureIcon}>
              <FaBullseye />
            </div>
            <h3>Smart Matching</h3>
            <p>Stop searching through hundreds of studies. We match your profile with clinical trials tailored to your condition and interests.</p>
          </div>
          <div className={styles.featureCard}>
            <div className={styles.featureIcon}>
              <FaLock />
            </div>
            <h3>Privacy Protected</h3>
            <p>Your privacy comes first. We never store, sell, or share your medical information.</p>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section id="how-it-works" className={styles.howItWorksSection}>
        <div className={styles.sectionHeader}>
          <h2 className={styles.sectionTitle}>How It Works</h2>
        </div>
        <div className={styles.stepsContainer}>
          <div className={styles.step}>
            <div className={styles.stepNumber}>1</div>
            <h3>Share Your Profile</h3>
            <p>Tell us about your interests, condition, and preferences</p>
          </div>
          <div className={styles.step}>
            <div className={styles.stepNumber}>2</div>
            <h3>Get Matched</h3>
            <p>Get personalized clinical trial recommendations from leading research institutions</p>
          </div>
          <div className={styles.step}>
            <div className={styles.stepNumber}>3</div>
            <h3>Connect & Participate</h3>
            <p>Review study details in plain language and decide which opportunities interest you</p>
          </div>
        </div>
      </section>

      {/* Our Story */}
      <section id="about-us" className={styles.storySection}>
        <div className={styles.sectionHeader}>
          <h2 className={styles.sectionTitle}>Our Story</h2>
        </div>
        <div className={`${styles.storyContent} ${styles.storyContentAligned}`}>
          <p className={styles.storyText}>
            Cureiosity was created to make discovering clinical research simple and empowering.
          </p>
          <p className={styles.storyText}>
            Millions of people are interested in exploring new treatment options, contributing to medical breakthroughs, or participating in paid research opportunities, yet finding the right clinical trial can be overwhelming. Information is often scattered across multiple sources, difficult to understand, and time consuming to navigate.
          </p>
          <p className={styles.storyText}>
            Cureiosity changes that. Our platform connects people with clinical trials from leading hospitals, universities, and research institutions through personalized matching and easy to understand study summaries.
          </p>
          <p className={styles.storyText}>
            We believe everyone should have access to research opportunities that align with their interests and needs. By simplifying the discovery process and removing barriers to participation, Cureiosity helps more people explore opportunities that could make a difference for themselves and for the future of medicine.
          </p>
        </div>
      </section>

      {/* Our Mission */}
      <section className={styles.missionSection}>
        <div className={styles.missionContent}>
          <h2 className={styles.missionTitle}>Our Mission</h2>
          <p className={styles.missionText}>
            Cureiosity's mission is to <span className={styles.missionHighlight}>empower patients</span> by helping them 
            discover clinical trials through a platform that is intuitive, friendly, and truly patient centric
          </p>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className={styles.testimonialsSection}>
        <div className={styles.testimonialsContainer}>
          <div className={styles.testimonialImage}>
            <img
              src="/every-trial-every-patient.png"
              alt="Doctor and patient illustration"
              className={styles.testimonialIllustration}
            />
          </div>
          <div className={styles.testimonialContent}>
            <h2 className={styles.testimonialTitle}>Every trial, Every patient</h2>
            <div className={styles.testimonialCard}>
              <p className={styles.testimonialText}>
                "I found a trial in my area within 10 minutes. The coordinator called me the same day.
                Cureiosity made the impossible feel possible."
              </p>
              <p className={styles.testimonialAuthor}>— Sarah M., Diabetes Patient</p>
            </div>
            <div className={styles.testimonialCard}>
              <p className={styles.testimonialText}>
                "As a physician, I appreciate having a trusted resource to recommend. My patients come back
                with quality options that actually fit their needs."
              </p>
              <p className={styles.testimonialAuthor}>— Dr. James Chen, Oncologist</p>
            </div>
          </div>
        </div>
      </section>

      {/* Email Collection Section */}
      <section className={styles.emailCollectionSection}>
        <div className={styles.emailSection}>
          <h2 className={styles.emailTitle}>Stay Updated on New Trials</h2>
          <p className={styles.emailDescription}>
            Get notified when new trials match your interests. No spam, just relevant opportunities delivered to your inbox
          </p>
          
          {!emailSubmitted ? (
            <form onSubmit={handleEmailSubmit} className={styles.emailForm}>
              <div className={styles.emailInputContainer}>
                <FaEnvelope className={styles.emailIcon} />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter your email address"
                  className={`${styles.emailInput} ${emailError ? styles.emailInputError : ''}`}
                />
                <button type="submit" className={styles.emailSubmitButton}>
                  Subscribe
                </button>
              </div>
              {emailError && <p className={styles.emailError}>{emailError}</p>}
            </form>
          ) : (
            <div className={styles.emailSuccess}>
              <FaCheckCircle className={styles.emailSuccessIcon} />
              <p>Thank you! We'll keep you updated on relevant trials.</p>
            </div>
          )}
        </div>
      </section>

      {/* Company Portal CTA */}
      <section className={styles.companyPortalSection}>
        <div className={styles.companyPortalContent}>
          <h2 className={styles.companyPortalTitle}>Are You a Clinical Trial Sponsor?</h2>
          <p className={styles.companyPortalSubtitle}>
            Post your clinical trials and reach qualified patients with our premium company portal
          </p>
          <div className={styles.companyPortalButtons}>
            <button 
              className={styles.companyPortalBtn} 
              onClick={() => navigate("/company-register")}
            >
              Get Started
            </button>
            <button 
              className={styles.companyLoginBtn} 
              onClick={() => navigate("/company-login")}
            >
              Sign In
            </button>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      {/*<section className={styles.finalCta}>
        <h2 className={styles.ctaTitle}>Ready to Find Your Trial?</h2>
        <p className={styles.ctaSubtitle}>
          Join us in making clinical trial discovery more human and accessible
        </p>
        <button className={styles.ctaButtonSecondary} onClick={handleNavigation}>
          Start Your Search Today
        </button>
      </section>*/}

      {/* Footer */}
      <footer className={styles.footer}>
        <div className={styles.footerContent}>
          <div className={styles.footerSection}>
            <h4 className={styles.footerTitle}>Cureiosity</h4>
            <p className={styles.footerText}>
              Every trial. Every patient. Every life matters
            </p>
          </div>
          <div className={styles.footerSection}>
            <h4 className={styles.footerTitle}>Quick Links</h4>
            <div className={styles.footerLinks}>
              <button 
                onClick={() => scrollToSection('how-it-works')} 
                className={styles.footerLink}
              >
                How It Works
              </button>
              
              <button 
                onClick={() => scrollToSection('about-us')} 
                className={styles.footerLink}
              >
                About Us
              </button>
              
            </div>
          </div>
          <div className={styles.footerSection}>
            <h4 className={styles.footerTitle}>Support</h4>
            <div className={styles.footerLinks}>
              <button 
                onClick={() => setShowPrivacy(true)} 
                className={styles.footerLink}
              >
                Privacy Policy
              </button>
              <button 
                onClick={() => setShowTerms(true)} 
                className={styles.footerLink}
              >
                Terms of Service
              </button>
            </div>
          </div>
          <div className={styles.footerSection}>
            <h4 className={styles.footerTitle}>Contact</h4>
            <div className={styles.footerLinks}>
              <p className={styles.footerText}>Questions or feedback?</p>
              <span className={styles.footerText}>contact@cureiosity.org</span>
            </div>
          </div>
        </div>
        <div className={styles.footerBottom}>
          <p className={styles.copyright}>© Cureiosity 2026. All rights reserved.</p>
        </div>
      </footer>

      {/* Terms of Service Modal */}
      <Modal isOpen={showTerms} onClose={() => setShowTerms(false)} title="Terms and Conditions">
        <div className={styles.legalContent}>
          <p><strong>Effective Date:</strong> 07/29/2025</p>
          
          <p>Welcome to <strong>Cureiosity.org</strong> ("we," "our," or "us"). By accessing or using our website, you agree to be bound by these Terms and Conditions ("Terms"). Please read them carefully. If you do not agree with these Terms, please do not use our website.</p>

          <h3>1. Purpose of the Website</h3>
          <p>Cureiosity.org is designed to help patients discover clinical trials through a user-friendly and patient-centric interface. We do <strong>not</strong> provide medical advice or act as a substitute for professional medical care.</p>
          <p>Always consult your doctor or healthcare provider before making decisions related to your health or participating in a clinical trial.</p>

          <h3>2. Eligibility</h3>
          <p>You must be at least 18 years old to use this website. By using the site, you confirm that you meet this age requirement.</p>

          <h3>3. Use of the Website</h3>
          <p>You agree to use this website only for lawful purposes and in a way that does not violate the rights of others or restrict or inhibit their use of the website.</p>
          <p>You may <strong>not</strong>:</p>
          <ul>
            <li>Use the site to transmit or upload viruses or malicious code.</li>
            <li>Attempt to access secure areas without authorization.</li>
            <li>Use any automated system to scrape or mine data from the site.</li>
          </ul>

          <h3>4. Content and Information</h3>
          <p>All content on Cureiosity.org is for <strong>informational purposes only</strong>. We strive to keep the information accurate and up-to-date, but we make <strong>no guarantees</strong> about its completeness, accuracy, or timeliness.</p>
          <p>We do not endorse or recommend any specific clinical trial, treatment, or healthcare provider.</p>

          <h3>5. Third-Party Links</h3>
          <p>Cureiosity.org may contain links to external websites or services that are not controlled by us. We are not responsible for the content, policies, or practices of any third-party websites.</p>

          <h3>6. Intellectual Property</h3>
          <p>All text, design, graphics, logos, and other content on this website are owned or licensed by Cureiosity and are protected by intellectual property laws.</p>
          <p>You may not copy, modify, distribute, or use our content without our written permission, except for personal and non-commercial use.</p>

          <h3>7. Privacy</h3>
          <p>Your privacy is important to us. Please read our Privacy Policy to understand how we collect, use, and protect your information.</p>

          <h3>8. Disclaimers</h3>
          <p>We provide the website <strong>"as is" and "as available"</strong>, without any warranties or guarantees, express or implied. We do not guarantee that the site will be error-free, uninterrupted, or secure.</p>

          <h3>9. Limitation of Liability</h3>
          <p>To the fullest extent permitted by law, Cureiosity is <strong>not liable</strong> for any indirect, incidental, special, or consequential damages resulting from your use of (or inability to use) the website.</p>

          <h3>10. Changes to These Terms</h3>
          <p>We may update these Terms at any time. If we do, we will post the updated version and revise the "Effective Date" at the top. Your continued use of the website means you accept the new Terms.</p>

          <h3>11. Governing Law</h3>
          <p>These Terms shall be governed by and construed under the laws of the State of Massachu, without regard to its conflict of law principles.</p>

          <h3>12. Contact Us</h3>
          <p>If you have any questions about these Terms, you can contact us at:<br />
          📧 support@cureiosity.org</p>
        </div>
      </Modal>

      {/* Privacy Policy Modal */}
      <Modal isOpen={showPrivacy} onClose={() => setShowPrivacy(false)} title="Privacy Policy">
        <div className={styles.legalContent}>
          <p><strong>Effective Date:</strong> 07/29/2025</p>
          
          <p>Cureiosity.org ("we," "our," or "us") respects your privacy and is committed to protecting your personal information. This Privacy Policy explains how we collect, use, and protect your information when you use our website.</p>

          <h3>1. What Information We Collect</h3>
          <p>We collect limited personal information when you:</p>
          <ul>
            <li>Fill out a contact form</li>
            <li>Sign up for our newsletter</li>
            <li>Interact with our website</li>
          </ul>
          <p><strong>This may include:</strong></p>
          <ul>
            <li>Name</li>
            <li>Email address</li>
            <li>Demographic information</li>
            <li>General location (if you choose to share it)</li>
            <li>Information about your interests related to clinical trials</li>
          </ul>
          <p>We do <strong>not</strong> collect sensitive health information unless explicitly provided by you through a contact form or other communication.</p>

          <h3>2. How We Use Your Information</h3>
          <p>We may use the information you provide to:</p>
          <ul>
            <li>Respond to your inquiries</li>
            <li>Send you updates or newsletters (if you opt in)</li>
            <li>Improve our website and user experience</li>
            <li>Analyze website traffic and usage trends (via cookies or analytics tools)</li>
          </ul>

          <h3>3. Sharing Your Information</h3>
          <p>We do <strong>not sell, rent, or share your personal information</strong> with third parties for marketing purposes.</p>
          <p>We may share your information only:</p>
          <ul>
            <li>With trusted service providers (e.g., email or analytics tools) who help us operate the website</li>
            <li>If required by law, legal process, or government request</li>
            <li>To protect our rights or prevent fraud and abuse</li>
          </ul>

          <h3>4. Cookies and Analytics</h3>
          <p>We may use cookies and third-party analytics tools (like Google Analytics) to collect anonymized data about how users interact with the site. This helps us understand how to improve our content and design.</p>
          <p>You can disable cookies in your browser settings at any time.</p>

          <h3>5. Your Choices</h3>
          <p>You can:</p>
          <ul>
            <li><strong>Unsubscribe</strong> from emails at any time by clicking the "unsubscribe" link</li>
            <li><strong>Request access, correction, or deletion</strong> of your personal data by contacting us</li>
            <li><strong>Disable cookies</strong> via your browser</li>
          </ul>
          <p>We respect your rights under applicable data privacy laws.</p>

          <h3>6. Data Security</h3>
          <p>We take reasonable steps to protect your personal information from loss, misuse, or unauthorized access. However, no method of transmission over the internet is 100% secure.</p>

          <h3>7. Children's Privacy</h3>
          <p>Cureiosity.org is intended for users age 18 and older. We do not knowingly collect information from children under 13. If we learn that we have collected data from a child, we will delete it promptly.</p>

          <h3>8. Changes to This Privacy Policy</h3>
          <p>We may update this Privacy Policy from time to time. If we make significant changes, we'll post them on this page and update the "Effective Date" above.</p>

          <h3>9. Contact Us</h3>
          <p>If you have any questions or concerns about this Privacy Policy or your personal information, please contact us at:<br />
          📧 support@cureiosity.org</p>
        </div>
      </Modal>
    </main>
  );
}

export default Welcome;
